import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import Tabs, { Tab } from 'Components/Molecules/Tabs/Tabs';
import useTransaction from 'Hooks/useTransaction';
import Loading from 'Components/Atoms/Loading/Loading';
import { formatCurrency, formatDateISO } from 'Utils/formatters';
import Icon from 'Components/Atoms/Icon/Icon';
import Tooltip from 'Components/Molecules/Tooltip/Tooltip';
import { cn } from 'Utils/cn';
import useLocale from 'Hooks/useLocale';
import Button from 'Components/Atoms/Button/Button';
import Divider from 'Components/Atoms/Divider/Divider';
import SelectContact from 'Components/Organisms/SelectContact/SelectContact';
import MultiselectTags from 'Components/Organisms/MultiselectTags/MultiselectTags';
import DatePicker from 'Components/Atoms/DatePicker/DatePicker';
import Input from 'Components/Atoms/Input/Input';
import Textarea from 'Components/Atoms/Textarea/Textarea';
import IconInfo from 'Components/Atoms/IconInfo/IconInfo';
import { useQueryClient } from '@tanstack/react-query';
import { CATEGORY_COLORS, DRAWER_STACK } from 'Utils/constants';
import DrawerStackSavingIndicator from 'Components/Organisms/DrawerStackProvider/DrawerStackSavingIndicator';
import useDrawerStack from 'Hooks/useDrawerStack';
import TransactionSplitEditorDrawer from '../TransactionSplitEditorDrawer/TransactionSplitEditorDrawer';
import { createCategory } from 'Services/categories';
import useProvidersConnections from 'Hooks/useProvidersConnections';
import TransactionEditorDrawerFiles from './TransactionEditorDrawerFiles';
import TransactionEditorDrawerInvoices from './TransactionEditorDrawerInvoices';
import DrawerStackConciliatedBadge from 'Components/Organisms/DrawerStackProvider/DrawerStackConciliatedBadge';

const TABS = {
    DATA: 'DATA',
    INVOICES: 'INVOICES',
    FILES: 'FILES',
};

export default function TransactionEditorDrawer(props) {
    const { transactionId, onClose } = props;

    const { t } = useTranslation('transactions');
    const locale = useLocale();
    const queryClient = useQueryClient();

    const [toolTab, setToolTab] = useState(TABS.DATA);
    const [isUpdatingFiles, setIsUpdatingFiles] = useState(false);

    const { transaction, isLoading, updateTransaction, isUpdating } = useTransaction(transactionId);
    const [description, setDescription] = useState('');
    const [comment, setComment] = useState('');
    const { provider_connection_product, date_booked, date_executed, parent_transaction_id, amount, currency } =
        transaction;

    const { open, register } = useDrawerStack();
    const { transaction: parentTransaction, isLoading: isLoadingParent } = useTransaction(parent_transaction_id);
    const { providersConnections } = useProvidersConnections();

    const providerName =
        providersConnections.find((provider) => provider.id === provider_connection_product?.provider_connection_id)
            ?.name ?? '';

    useEffect(() => {
        const unregister = register(DRAWER_STACK.TRANSACTION_SPLIT_EDITOR, TransactionSplitEditorDrawer);
        return () => {
            unregister();
        };
    }, []);

    useEffect(() => {
        if (!transaction?.description) return;

        setDescription(transaction.description);
        setComment(transaction.comment ?? '');
    }, [transaction]);

    const isExpense = amount * (transaction.reverse_sign ? -1 : 1) < 0;

    const handleDescriptionBlur = () => {
        if (description === transaction.description) return;

        if (!description) {
            setDescription(transaction.description);
            return;
        }

        updateTransaction({ description });
    };

    const handleCommentBlur = () => {
        if (comment === transaction.comment) return;

        updateTransaction({ comment: comment ? comment : null });
    };

    const handleDateChange = (ev) => {
        const date = ev.target.value;
        if (!date) return;

        updateTransaction({ date_booked: date });
    };

    const handleDeleteTransaction = () => {
        open(DRAWER_STACK.TRANSACTION_DELETE, {
            transaction,
            title: t('delete-transaction-title'),
            onSubmit: () => onClose(),
        });
    };

    const handleSplitTransaction = () => {
        open(DRAWER_STACK.TRANSACTION_SPLIT_EDITOR, {
            transactionId,
            title: t('split-transaction-title'),
        });
    };

    const handleSplitParentTransaction = () => {
        open(DRAWER_STACK.TRANSACTION_SPLIT_EDITOR, {
            transactionId: parent_transaction_id,
            title: t('original-transaction'),
        });
    };

    const handleCreateContact = () => {
        open(DRAWER_STACK.CONTACT_EDITOR, {
            contact: {},
            title: t('create-contact'),
            onSubmit: async (savedContact) => {
                updateTransaction({ contact_id: savedContact.id });
            },
            drawerStackProps: {
                size: 'lg',
            },
        });
    };

    const handleAddTag = () => {
        const randomColor = Math.floor(Math.random() * CATEGORY_COLORS.length);
        const color = CATEGORY_COLORS[randomColor];

        open(DRAWER_STACK.TAG_EDITOR, {
            title: t('create-tag'),
            tag: {
                name: '',
                color,
                icon: '💰',
            },
            onSubmit: async (data) => {
                const tag = await createCategory(data);
                await queryClient.invalidateQueries({ queryKey: ['categories'] });

                const taxonomies_tags_ids = [...(transaction.tags_ids || []), tag.id];
                updateTransaction({ taxonomies_tags_ids });
            },
            drawerStackProps: {
                size: 'lg',
            },
        });
    };

    const isSaving = isUpdating || isUpdatingFiles;

    if (isLoading) {
        return (
            <div className="h-full flex justify-center items-center">
                <Loading />
            </div>
        );
    }

    return (
        <div>
            <DrawerStackSavingIndicator saving={isSaving} />
            {transaction.is_reconciled && (
                <DrawerStackConciliatedBadge>
                    <span className="inline-flex gap-1.5 text-success items-center text-sm">
                        <div className="bg-success/10 text-success size-6 rounded-full flex items-center justify-center">
                            <Icon name="check" size={12} color="currentColor" />
                        </div>
                        <span>{t('reconciliated')}</span>
                    </span>
                </DrawerStackConciliatedBadge>
            )}

            <Tabs className="w-full" value={toolTab} onChange={setToolTab}>
                <Tab value={TABS.DATA}>{t('data')}</Tab>
                <Tab value={TABS.INVOICES}>{t('invoices')}</Tab>
                <Tab value={TABS.FILES}>{t('files')}</Tab>
            </Tabs>

            {toolTab === TABS.DATA && (
                <div>
                    {parent_transaction_id && !isLoadingParent && (
                        <div className="pt-4 -mb-1 md:-mb-4 flex justify-end">
                            <Button size="extraSmall" onClick={handleSplitParentTransaction}>
                                <Icon name="arrow-left-small" size={18} color="currentColor" />
                                <div>{parentTransaction.description}</div>
                            </Button>
                        </div>
                    )}

                    <div className="flex justify-between items-center py-4 md:py-8">
                        <div className="flex items-center gap-2">
                            <img src={provider_connection_product?.provider_logo_url} width={32} height={32} alt="" />
                            <div>
                                <div className="font-semibold text-base md:text-lg">{providerName}</div>
                                <div className="text-gray-500 text-xs">{provider_connection_product?.name}</div>
                            </div>
                        </div>
                        <div>
                            <div
                                className={cn(
                                    'font-mono flex justify-end gap-1 items-center font-medium',
                                    !isExpense && 'text-income',
                                    transaction.exclude_accounting && 'opacity-50',
                                )}
                            >
                                {transaction.exclude_accounting && (
                                    <Tooltip label={t('ignored')}>
                                        <span className="flex">
                                            <Icon name="ignore" size={18} color="currentColor" />
                                        </span>
                                    </Tooltip>
                                )}

                                {transaction.reverse_sign && (
                                    <Tooltip label={t('modified-sign')}>
                                        <span className="flex">
                                            <Icon name="reverse" size={16} color="currentColor" />
                                        </span>
                                    </Tooltip>
                                )}

                                {!!parent_transaction_id && (
                                    <Tooltip
                                        label={<span className="font-normal text-xs">{t('split-transaction')}</span>}
                                    >
                                        <div className="shrink-0 flex">
                                            <Icon name="split" size={20} color="currentColor" />
                                        </div>
                                    </Tooltip>
                                )}

                                {formatCurrency(amount, { currency, locale })}
                            </div>
                            {transaction.exclude_accounting && (
                                <div className="text-xs opacity-50 text-right">({t('ignored')})</div>
                            )}
                        </div>
                    </div>

                    <div
                        className={cn(
                            'flex gap-2 flex-wrap md:justify-between',
                            !!parent_transaction_id && 'md:justify-start',
                        )}
                    >
                        <Tooltip label={t('ignore-tooltip')}>
                            <Button
                                size="extraSmall"
                                onClick={() =>
                                    updateTransaction({ exclude_accounting: !transaction.exclude_accounting })
                                }
                            >
                                <Icon name="ignore" size={16} color="currentColor" />
                                {transaction.exclude_accounting ? t('do-not-ignore') : t('ignore')}
                            </Button>
                        </Tooltip>

                        <Tooltip label={t('mark-as-return-tooltip')}>
                            <Button
                                size="extraSmall"
                                onClick={() => updateTransaction({ reverse_sign: !transaction.reverse_sign })}
                            >
                                <Icon name="reverse" size={12} color="currentColor" />
                                {t('mark-as-return')}
                            </Button>
                        </Tooltip>

                        {!parent_transaction_id && (
                            <Tooltip label={t('split-tooltip')}>
                                <Button size="extraSmall" onClick={handleSplitTransaction}>
                                    <Icon name="split" size={16} color="currentColor" />
                                    {t('split')}
                                </Button>
                            </Tooltip>
                        )}

                        <Button size="extraSmall" onClick={handleDeleteTransaction}>
                            <Icon name="trash" size={16} color="currentColor" />
                            {t('delete')}
                        </Button>
                    </div>

                    <Divider className="my-5" />

                    <div className="grid gap-5 pt-1">
                        <SelectContact
                            value={transaction.contact_id}
                            label={t('contact')}
                            onChange={(contact_id) => updateTransaction({ contact_id: contact_id ? contact_id : null })}
                            onAdd={handleCreateContact}
                        />
                        <MultiselectTags
                            label={t('tags')}
                            value={transaction.tags_ids}
                            onChange={(tags_ids) => updateTransaction({ taxonomies_tags_ids: tags_ids })}
                            onAdd={handleAddTag}
                        />
                        <div className="flex gap-2 items-center">
                            <DatePicker
                                value={formatDateISO(date_booked)}
                                label={t('date')}
                                onChange={handleDateChange}
                            />
                            {date_executed && date_executed.getUTCDate() !== date_booked.getUTCDate() && (
                                <div className="px-1 flex">
                                    <IconInfo
                                        className="size-7"
                                        tooltip={t('executed-on', {
                                            date: date_executed.toLocaleDateString(locale),
                                            interpolation: { escapeValue: false },
                                        })}
                                    />
                                </div>
                            )}
                        </div>
                        <Input
                            label={t('concept')}
                            value={description}
                            onChange={(ev) => setDescription(ev.target.value)}
                            onBlur={handleDescriptionBlur}
                        />
                        <Textarea
                            label={t('description')}
                            placeholder={t('description-placeholder')}
                            value={comment}
                            onChange={(ev) => setComment(ev.target.value)}
                            onBlur={handleCommentBlur}
                        />
                    </div>
                </div>
            )}

            {toolTab === TABS.FILES && (
                <TransactionEditorDrawerFiles transactionId={transactionId} onToggleUploading={setIsUpdatingFiles} />
            )}

            {toolTab === TABS.INVOICES && <TransactionEditorDrawerInvoices transactionId={transactionId} />}
        </div>
    );
}

TransactionEditorDrawer.propTypes = {
    onClose: PropTypes.func,
    transactionId: PropTypes.string.isRequired,
};

TransactionEditorDrawer.drawerStackProps = {
    size: 'lg',
};
